/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {computed} from '@preact/signals-react';
import {ColumnState} from 'ag-grid-community';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import {toggleUserPrompt} from '@ion/applications/src/store/actions/user-prompt';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {getVehicleProfileCompatibility, promotePidsToProduction} from '@ion/core/src/services/Connect';
import {BusProfile, GenericObject, ValidationSummary} from '@ion/core/src/types/entities';
import {PromotePidsParams} from '@ion/core/src/types/services';
import {dateFormatter, timeDiff, timeFormatted} from '@ion/core/src/utils/DateUtil';
import {makePreview, MakePreviewConfig} from '@ion/dls/src/components/search/PreviewUtils';
import {SortModelType} from '@ion/dls/src/grid/IonGridUtils';
import {userPreferredTimeFormatter} from '@ion/dls/src/utils/ComponentUtils';
import {
  AttributeOperators,
  AttributeTypes,
  Mapping,
  QueryChipData,
  SuggestionsType
} from '@ion/features/src/search/types';
import {activeProfileTab} from './profile/store/store';
import {Context} from './types';

type Vehicle = {
  vin: string;
  validations: ValidationSummary[];
};

export interface ParsedResult {
  paramNames: string;
  passCount: string;
}

export type Filter = {
  field: string;
  keyword: string
};

export type TripTimeRange = {
  startTime: string | null;
  endTime: string | null;
  durationInSeconds: number | null;
};

type Option = {
  value: string;
  label: string
};

export type ColumnStateUpdater = {
  currentState: ColumnState[] | null;
  setState: (state: ColumnState[]) => void;
  setHasUpdate: (flag: boolean) => void;
};

export const MIN_PASS_COUNT = 80;
export const NOT_VALIDATED  = 'NOT_VALIDATED';

const NUMERIC_FIELDS = new Set([
  'busProfileId',
  'busSpeed',
  'byteLength',
  'byteStartPosition',
  'bitLength',
  'bitStartPosition'
]);

export const BUS_PROFILE_FIELDS: Array<{field: string; label: string; type: 'string'|'number'}> = [
  {
    field : 'busProfileId',
    label : 'Bus Profile Id',
    type  : 'number'
  },
  {
    field : 'busProfileName',
    label : 'Bus Profile Name',
    type  : 'string'
  },
  {
    field : 'firmwareParameterName',
    label : 'Firmware Parameter Name',
    type  : 'string'
  },
  {
    field : 'busTypeName',
    label : 'Bus Type',
    type  : 'string'
  },
  {
    field : 'busSpeed',
    label : 'Bus Speed',
    type  : 'number'
  },
  {
    field : 'busTxHex',
    label : 'Bus Tx Hex',
    type  : 'string'
  },
  {
    field : 'busRxHex',
    label : 'Bus Rx Hex',
    type  : 'string'
  },
  {
    field : 'byteLength',
    label : 'Byte Length',
    type  : 'number'
  },
  {
    field : 'byteStartPosition',
    label : 'Byte Start Position',
    type  : 'number'
  },
  {
    field : 'bitLength',
    label : 'Bit Length',
    type  : 'number'
  },
  {
    field : 'bitStartPosition',
    label : 'Bit Start Position',
    type  : 'number'
  },
  {
    field : 'requestMessageHex',
    label : 'Request Message Hex',
    type  : 'string'
  },
  {
    field : 'initializeMessageHex',
    label : 'Initialize Message Hex',
    type  : 'string'
  },
  {
    field : 'keepAliveMessageHex',
    label : 'Keep Alive Message Hex',
    type  : 'string'
  },
  {
    field : 'exitMessageHex',
    label : 'Exit Message Hex',
    type  : 'string'
  },
  {
    field : 'formulaName',
    label : 'Formula Name',
    type  : 'string'
  }
];

/**
 * Validates that every vehicle in the data has all its validations' passCount >= 80.
 *
 * @param {Vehicle[]} data - The array of vehicles with validation data.
 * @returns {boolean} - Returns true if all validations pass, otherwise false.
 */
export const validateRangeCheck = (data: Vehicle[]): boolean => {
  return data?.every?.((vehicle) =>
    vehicle?.validations?.every(validation => validation?.passPercentage >= MIN_PASS_COUNT)
  );
};

/**
 * Validates that the specified validation by `paramId` for each vehicle in the data 
 * has a passPercentage >= MIN_PASS_COUNT.
 *
 * @param {Vehicle[]} data - The array of vehicles with validation data.
 * @param {string} id - The ID of the specific validation to check.
 * @returns {boolean} - Returns true if the specified validation passes for all vehicles, otherwise false.
 */
export const validateRangeCheckById = (data: Vehicle[], id: number): boolean => {
  return data?.every?.((vehicle) =>
    vehicle?.validations?.some((validation) =>
      validation?.paramId === id && validation?.passPercentage >= MIN_PASS_COUNT
    )
  );
};

/**
 * Field replacements for mapping the sort model fields to the API fields.
 */
const fieldReplacements = {
  vin       : 'vin',
  year      : 'yearString',
  make      : 'makeString',
  model     : 'modelString',
  engine    : 'engineString',
  fuel      : 'fuelString',
  createdOn : 'created_date'
};

/**
 * Converts a sort model into a format compatible with the Profiles: Search API
 * using +searchableProperty or -searchableProperty format.
 *
 * @param {SortModelType[]} sortModel - The sort model array from IonGrid.
 * @param {GenericObject} replacements - Optional field replacements for mapping.
 * @returns {string | null} - The converted sort string or null if no valid sort model.
 */
export const sortModelConverter = (
  sortModel: SortModelType[],
  replacements: GenericObject = {}
): string | null => {
  if (!sortModel?.length) return null;
  return sortModel
    .map(({colId, sort}) => {
      const field  = fieldReplacements?.[colId] || colId;
      const prefix = sort?.toLowerCase() === 'desc' ? '-' : '+';

      return `${prefix}${field}`;
    })
    .join(',');
};

/**
 * Formats a vehicle validation event time into a user-friendly date and time
 * string using the system's preferred date and time formats.
 *
 * @param eventTime The ISO 8601 formatted event time string to format.
 * @returns        A formatted string combining date and time, or empty string
 *                 if eventTime is not provided.
 */
export const formatVehicleValidationDateTime = (eventTime?: string | null): string => {
  if (eventTime) {
    const preferredDateFormat = UserPreferences.getSystemDateFormat();
    const formattedDate       = dateFormatter(eventTime, preferredDateFormat);
    const formattedTime       = userPreferredTimeFormatter(eventTime, true);
    return `${formattedDate} ${formattedTime}`;
  }
  return '';
};

/**
 * Calculates the time range for a trip by extracting start time, end time, and
 * duration from an array of validation events. Events are sorted chronologically
 * to determine the trip boundaries.
 *
 * @param events An array of validation event objects containing eventTime.
 * @returns      A TripTimeRange object with startTime, endTime, and
 *               durationInSeconds, or null values if no events provided.
 */
export const getTripTimeRange = (events = []): TripTimeRange => {
  const eventTimes = events
    .map(({eventTime}) => eventTime)
    .filter(Boolean)
    .sort();
  if (!eventTimes.length) {
    return {
      startTime         : null,
      endTime           : null,
      durationInSeconds : null
    };
  }
  const startTime         = eventTimes[0];
  const endTime           = eventTimes[eventTimes.length - 1];
  const durationInSeconds = Math.max(0, Math.round(timeDiff(endTime, startTime, 'seconds')));
  return {
    startTime,
    endTime,
    durationInSeconds
  };
};

/**
 * Formats a trip time range into a human-readable string displaying start time,
 * end time, and duration. Uses the system's preferred date and time formats.
 * Handles same-day trips by omitting the end date.
 *
 * @param tripTimeRange The TripTimeRange object containing start, end, and
 *                      duration information.
 * @param translateFn   Optional translation function for formatting duration
 *                      and unavailable messages (default: translate).
 * @returns             A formatted string like "01/15/2025 10:30 AM - 11:45 AM
 *                      (1h 15m)" or "unavailable" if data is incomplete.
 */
export const formatTripTimeRange = (tripTimeRange: TripTimeRange, translateFn: Function = translate): string => {
  const {
    startTime,
    endTime,
    durationInSeconds
  } = tripTimeRange || {};
  if (!startTime || !endTime || isNil(durationInSeconds)) {
    return translateFn('common:unavailable');
  }
  const preferredDateFormat = UserPreferences.getSystemDateFormat();
  const formattedStartDate  = dateFormatter(startTime, preferredDateFormat);
  const formattedEndDate    = dateFormatter(endTime, preferredDateFormat);
  const formattedStartTime  = userPreferredTimeFormatter(startTime, true);
  const formattedEndTime    = userPreferredTimeFormatter(endTime, true);
  const formattedStart      = `${formattedStartDate} ${formattedStartTime}`;
  const duration            = timeFormatted(durationInSeconds, translateFn);
  const isSameDay           = formattedStartDate === formattedEndDate;
  const formattedEnd        = isSameDay ? formattedEndTime : `${formattedEndDate} ${formattedEndTime}`;
  return `${formattedStart} - ${formattedEnd} (${duration})`;
};

/**
 * Processes the validation results to group them by trip and parameter.
 */
export function processValidationResults(vehicleDetails) {
  const {validationResults, pidSummary} = vehicleDetails?.[0]?.vehicle || {};
  const pidSummaryByParamId = new Map(
    (pidSummary || []).map((summary) => [summary?.paramId, summary])
  );

  const groupedResults = validationResults?.reduce((acc, result) => {
    const tripKey = `trip${result?.tripNumber}`;
    if (!acc[tripKey]) acc[tripKey] = [];
    acc[tripKey].push(result);
    return acc;
  }, {}) || {};

  const predefinedTrips = Object.keys(groupedResults);

  return predefinedTrips.map(trip => {
    const events = groupedResults[trip] || [];
    const oemParametersMap = new Map();
    const tripTimeRange = getTripTimeRange(events);

    // Find the minimum sequence number for this trip to normalize indexing
    const minSequenceNumber = events.length > 0 ? Math.min(...events.map(e => e.sequenceNumber)) : 1;

    events.forEach(event => {
      event?.oemParameters?.forEach(param => {
        if (!oemParametersMap.has(param.param)) {
          const paramSummary = pidSummaryByParamId.get(param.param);
          oemParametersMap.set(param.param, {
            oemParameterName : param.name,
            oemParameterId   : param.param,
            pidStatus        : paramSummary?.status ?? null,
            data             : Array(10).fill(null)
          });
        }
      });

      event.oemParameters?.forEach(param => {
        const oemParam = oemParametersMap.get(param.param);
        // Normalize sequence number to start from 0 for each trip
        const index = event.sequenceNumber - minSequenceNumber;
        if (index >= 0 && index < 10) {
          oemParam.data[index] = {
            value            : param.value ?? null,
            validationStatus : param.validationStatus ?? null,
            errorMessage     : param.errorMessage ?? null,
            eventTime        : event.eventTime ?? null
          };
        }
      });
    });

    return {
      trip,
      tripTimeRange,
      data : Array.from(oemParametersMap.values())
    };
  });
}

/**
 * Parses the grouped validations to get the unique param names and pass count.
 * 
 * @param groupedValidations - The grouped validations.
 * @returns The parsed result with param names and pass count, or null if no validations are available.
 */
export const parseValidations = function(groupedValidations: ValidationSummary[]): ParsedResult | null {
  if (!groupedValidations?.length) {
    return {
      paramNames : null,
      passCount  : null
    };
  }
  const allValidations = groupedValidations.flat();
  const notValidated   = allValidations.filter((validation) => validation?.validationStatus === NOT_VALIDATED).length;
  const paramNames     = allValidations.map((validation) => validation?.paramName).filter(Boolean).join(', ');
  const totalCount     = allValidations.length;

  if (!paramNames || totalCount === 0) {
    return {
      paramNames : null,
      passCount  : null
    };
  }
  return {
    paramNames,
    passCount : `${notValidated}/${totalCount}`
  };
};

/**
 * Handles promoting PIDs to production by calling the respective API endpoint.
 */
export const handlePromotePidsToProduction = async({
  year,
  make,
  model,
  engine,
  fuelType,
  busProfileIds
}: PromotePidsParams) => {
  try {
    const response = await promotePidsToProduction({
      year,
      make,
      model,
      engine,
      fuelType,
      busProfileIds
    });
    if (isEmpty(response)) {
      error(`No response when attempting to Promote PIDs ${busProfileIds}:`, response);
      throw new Error('No response when attempting to Promote PIDs');
    }
    return response;
  } catch (err) {
    error('Error promoting PIDs to production:', err);
    throw err;
  }
};

/**
 * Extracts the range check rule message from validation rule data.
 */
export const extractRangeCheckRuleMessage = function(data: any): string | null {
  const message = data?.[0]?.validationRule?.rule?.event?.params?.message;
  return message?.trim() !== '' ? message : null;
};

/**
 * Formats the title property for display.
 */
export const formatTitleProperty = (property: string | number, includeSeparator: boolean = false): string => {
  return !isNil(property) ? `${property}${includeSeparator ? ' -' : ''}` : '';
};

/**
 * Filters the parameters based on the bus profiles and status (test or prod).
 */
export const getFilteredParameters = (busProfiles: BusProfile[] = [], status: 'test' | 'prod') => {
  const filteredProfiles = busProfiles.filter(
    (profile) => profile?.status?.toLowerCase() === status.toLowerCase()
  );
  const uniqueParameters = Array.from(
    new Set(filteredProfiles.map((profile) => profile.firmwareParameterEnumValue))
  );
  return uniqueParameters;
};

/**
 * Handles Downloading Vehicle Compatibility Report
 * by calling the respective API endpoint.
 */
export const onViewProfileCompatibility = () => {
  return async(dispatch: Function): Promise<any> => {
    try {
      const downloaded = await getVehicleProfileCompatibility();
      if (downloaded[0]?.s3DownloadUrl) {
        const url = downloaded[0]?.s3DownloadUrl;
        window.open(url, '_blank');
      }
      dispatch(toggleUserPrompt({
        message     : translate('common:downloadReportSuccess'),
        autoHide    : false,
        actionLabel : translate('common:ok')
      }));
    } catch (err) {
      error('Error downloading compatibility report:', err);
      dispatch(toggleUserPrompt({
        message     : translate('common:downloadReportFailed'),
        actionLabel : translate('common:ok'),
        autoHide    : false
      }));
    }
  };
};

export const mapOptions = (options: any[] | null | undefined, key: string): Option[] =>
  Array.isArray(options)
    ? options.map((param) => {
      const val = param?.[key];
      return {value: String(val ?? ''), label: String(val ?? '')};
    })
    : [];

export const getFirmwareOptions         = (options) => mapOptions(options, 'FirmwareParameterName');
export const getBusTypeOptions          = (options) => mapOptions(options, 'BusTypeName');
export const getBusTypeExtensionOptions = (options) => mapOptions(options, 'BusTypeExtensionName');
export const getBusSpeedOptions         = (options) => mapOptions(options, 'BusSpeedName');
export const getBusFormulaOptions       = (options) => mapOptions(options, 'BusFormulaName');

/**
 * Builds a searchTerms object for the Bus Profiles search API.
 * Numeric fields are passed as-is, string fields are wrapped with wildcards (*...*).
 */
export function buildBusProfileSearchTerms(filters: Filter[]) {
  const searchTermsByField: Record<string, string | number> = {};
  for (const {field, keyword} of filters ?? []) {
    const trimmedKeyword = String(keyword ?? '').trim();
    if (!trimmedKeyword) continue;
    if (NUMERIC_FIELDS.has(field)) {
      const numericKeyword = Number(trimmedKeyword);
      if (!Number.isNaN(numericKeyword)) {
        searchTermsByField[field] = numericKeyword;
      }
    } else {
      searchTermsByField[field] = `*${trimmedKeyword}*`;
    }
  }
  return searchTermsByField;
}

/**
 * Creates search options for the given input and fields.
 * 
 * @param   input - the search input
 * @param   fields - array of fields to search
 * @param   previewConfig - configuration for the preview
 * @returns an array of search options
 */
export const createSearchOptions = (
  input: string,
  fields: { field: string; label: string }[],
  previewConfig: MakePreviewConfig = {maxTokensToShow: 2}
) =>
  fields.map(({field, label}) => {
    const preview = makePreview(input, previewConfig);
    return {
      item : field,
      text : input,
      name : `${preview} in ${label}`
    };
  });

/**
 * formatKeywords - Converts a comma-separated string into an array of formatted items.
 *
 * @param {string} str - The input string containing keywords separated by commas.
 * @returns {string[]} - An array of strings where each keyword is wrapped in asterisks (*keyword*).
 */
function formatKeywords(str) {
  if (str) {
    const items = Array.isArray(str) ? str : [str];
    return items?.flatMap(item => item?.split(/[,]+/)?.map(item => `*${item?.trim()}*`));
  }
  return [];
}

/**
 * Processes an array of filters and returns a search terms object.
 * @param {Array} filters - Array of filter objects with `field` and `keyword` properties.
 * @returns {Object} searchTerms - Object mapping fields to arrays of keywords.
 */
export function extractSearchTerms(filters = []) {
  return filters?.reduce((acc, filter) => {
    const {attribute, value} = filter || {};
    const transformed = formatKeywords(value);
    if (attribute && transformed) {
      acc[attribute] = attribute !== 'fuel' ? transformed : `*${value}*`;
    }
    return acc;
  }, {});
};

export enum SearchFields {
  VIN = 'vin',
  YEAR = 'year',
  MAKE = 'make',
  MODEL = 'model',
  ENGINE = 'engine',
  ESN = 'esn',
  FUEL = 'fuel',
  USERNAME = 'username',
  STATUS = 'status'
};

/**
 * Creates a mapping configuration for a text-based search attribute that checks if a field contains a given entry.
 *
 * @param field - The search field to which this mapping applies.
 * @param entry - The text value used for display and translation in the mapping label.
 * @param options - Optional settings:
 *                - hasNoCharacterLimit: Indicates if the text
 *                input should bypass character limit validation (default: false).
 *                - toolTipText: When provided, shows an info tooltip with this text in the search input.
 *                - isExact: Shows the `is` operator rather than `contains`, for a field
 *                which is always matched exactly (default: false).
 */
export const createTextContainsMapping = (
  field: SearchFields | string,
  entry: string,
  options?: {hasNoCharacterLimit?: boolean, isExact?: boolean, toolTipText?: string}
): Mapping => ({
  type                : AttributeTypes.TEXT,
  suggestion          : SuggestionsType.TEXT,
  label               : translate(entry),
  key                 : field,
  hasNoCharacterLimit : options?.hasNoCharacterLimit || false,
  attributes          : options?.isExact
    ? {
      label : translate('common:modularsearch.attributes.is'),
      value : AttributeOperators.IS
    }
    : {
      label : translate('common:modularsearch.attributes.contains'),
      value : AttributeOperators.CONTAINS
    },
  ...(options?.toolTipText && {hasToolTip: true, toolTipText: options.toolTipText})
});

/**
 * Shallow ordered equality for arrays of `QueryChipData` using JSON stringify.
 *
 * @param a - First array to compare.
 * @param b - Second array to compare.
 * @returns `true` if both arrays have the same length and each element
 *          stringifies equal at the same index; otherwise `false`.
 */
export const areEqual = (a: QueryChipData[] = [], b: QueryChipData[] = []): boolean => {
  const isSameLength = a.length === b.length;
  // Use .every() to check if all elements match. It only proceeds if lengths match.
  const allElementsMatch = isSameLength && a.every((val, index) => {
    return JSON.stringify(val) === JSON.stringify(b[index]);
  });
  return allElementsMatch;
};

// returns boolean value based on the test / production tab selected ...
export const isTest       = computed(() => activeProfileTab.value === Context.test);
export const isProduction = computed(() => activeProfileTab.value === Context.production);

/**
 * Normalizes column state to only the fields that represent intentional user
 * changes (visibility and order), ignoring layout-only properties like width
 * that ag-grid adjusts automatically (e.g. when a scrollbar appears).
 */
export const normalizeColumnState = (state: ColumnState[]) => (
  state?.map(({colId, hide}) => ({
    colId,
    hide : hide === true
  }))) ?? [];

/**
 * Shared handler for column state updates across Vehicle Manager, Profile Manager and Validation Rules.
 *
 * @param columnState - The new column state received from the grid.
 * @param updater - Object containing the current state signal value and the setters to apply the update.
 */
export const onColumnStateUpdate = (
  columnState: ColumnState[],
  {
    currentState,
    setState,
    setHasUpdate
  }: ColumnStateUpdater
): void => {
  setState(columnState);
  if (isNil(currentState)) {
    setHasUpdate(false);
  } else {
    const hasStateChanged = !isEqual(normalizeColumnState(columnState), normalizeColumnState(currentState));
    if (hasStateChanged) {
      setHasUpdate(true);
    }
  }
};
